import { MouseEvent, ReactNode } from 'react';
import { OnChange } from 'react-final-form-listeners';
import classNames from 'classnames/bind';
import { GENERAL_ICONS, COLORS } from 'components/GeneralIcon/GeneralIcon';
import IconButton, { ALIGN_TYPES } from 'components/IconButton/IconButton';
import { formatString } from 'strings';
import styles from './CheckboxCard.module.css';

type CheckboxCardProps = {
  name: string;
  title: string;
  selected?: boolean;
  showTrashButton?: boolean;
  disabled?: boolean;
  plus?: boolean;
  showCheckmark?: boolean;
  noBorder?: boolean;
  error?: boolean;
  multiple?: boolean;
  cursorDisabled?: boolean;
  children?: ReactNode;
  size?: 'small' | 'medium';
  onClick?: () => void;
  onTrashClick?: () => void;
  className?: string;
  titleClassName?: string;
};

const cx = classNames.bind(styles);

export function CheckboxCard({
  name,
  title,
  selected,
  error,
  multiple,
  cursorDisabled,
  showTrashButton,
  showCheckmark,
  disabled,
  plus,
  children,
  size,
  noBorder,
  onClick,
  onTrashClick,
  className,
  titleClassName,
}: CheckboxCardProps): JSX.Element {
  function handleTrashIconClick(e: MouseEvent<unknown>) {
    e.stopPropagation();

    // Both are optional props, and the other call sites in this file already
    // guard with `?.`.
    if (selected) {
      onTrashClick?.();
    } else {
      onClick?.();
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={cx('checkbox-card', className, {
        selected,
        disabled,
        error,
        multiple,
        size,
        'no-border': noBorder,
        'disable-cursor': cursorDisabled,
      })}
      data-testid={name}
      onClick={onClick}
      onKeyDown={(e) => {
        const tag = (e.target as HTMLElement).tagName;
        if (
          (e.key === 'Enter' || e.key === ' ') &&
          tag !== 'INPUT' &&
          tag !== 'TEXTAREA'
        ) {
          onClick?.();
        }
      }}
    >
      {name && <OnChange name={`${name}.selected`}>{() => {}}</OnChange>}
      <div className="flex justify-between">
        <div>
          <div
            className={cx(
              'title',
              {
                disabled,
                selected,
                showPlus: plus,
                'text-dark-gray': !selected,
                'text-navy': selected,
              },
              titleClassName,
            )}
          >
            {plus && <span className={cx('show-icon')} />}
            <div
              className={cx('text', 'flex items-start gap-2 pl-0', {
                hasIcon: showCheckmark || plus,
              })}
            >
              {showCheckmark && (
                <div
                  className="leading-none shrink-0"
                  style={{ marginTop: '1px' }}
                >
                  <IconButton
                    icon={
                      selected
                        ? GENERAL_ICONS.CHECKBOX_SELECTED
                        : GENERAL_ICONS.CHECKBOX_UNSELECTED
                    }
                    generalIconProps={{
                      color: selected ? COLORS.NAVY : COLORS.GRAY,
                      hoverColor: COLORS.NAVY,
                    }}
                    onClick={handleTrashIconClick}
                  />
                </div>
              )}
              <span className="leading-normal">{formatString(title)}</span>
            </div>
          </div>
        </div>

        {showTrashButton && (
          <IconButton
            align={ALIGN_TYPES.LEFT}
            icon={GENERAL_ICONS.TRASH}
            onClick={handleTrashIconClick}
          />
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}
